import { useNavigate } from "react-router-dom";
import { Paper, Button, Group, Stack, Center, Loader, Text, ThemeIcon, ScrollArea, useMantineTheme, Badge, ActionIcon } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { IconClock, IconClockPlay, IconClockPause, IconTrash } from "@tabler/icons-react";
import cx from "clsx";

import { ListWorkOrderScheduleDto, RecurrenceType } from "../../models/workorderschedule.models";
import { useFilteredWorkOrderSchedules } from "../../functions/workorderschedule-filter.hooks";
import { useSearch } from "../../functions/SearchBox.hook";
import { NoSearchResults } from "../Shared/NoSearchResults";
import classes from './ScheduleList.module.css';
import { getRecurrenceTypeDescription, getScheduleStatusColor, getScheduleStatusDescription } from "../../functions/schedule.functions";

interface ScheduleListProps {
	schedules: ListWorkOrderScheduleDto[] | undefined;
	isLoading: boolean;
	selectedScheduleId?: string | null;
	onSelectSchedule?: (scheduleId: string) => void;
}

export const ScheduleList = ({ schedules, isLoading, selectedScheduleId, onSelectSchedule }: ScheduleListProps) => {
	const navigate = useNavigate();
	const theme = useMantineTheme();
	const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.xs})`, true);
	const { query, setQuery } = useSearch();
	const { filteredSchedules, showNoResults } = useFilteredWorkOrderSchedules(schedules || []);

	if (isLoading) return <Paper withBorder p={0} radius="md" style={{ overflow: 'hidden' }}>
		<ScrollArea type={isMobile ? "never" : "scroll"} style={{ height: isMobile ? 'unset' : 'calc(100vh - 160px)', minHeight: isMobile ? 'calc(100vh - 150px)' : 'calc(100vh - 160px)' }}>
			<Center>
				<Loader size="sm" mt="lg" />
			</Center>
		</ScrollArea>
	</Paper>

	const handleScheduleSelect = (e: React.MouseEvent<HTMLAnchorElement>, scheduleId: string): void => {
		e.preventDefault();
		onSelectSchedule?.(scheduleId);
		navigate(`/workorder-schedules/${scheduleId}`);
	}

	return <Paper withBorder p={0} radius="md" style={{ overflow: 'hidden' }}>
		<ScrollArea type={isMobile ? "never" : "hover"} style={{ height: isMobile ? 'unset' : 'calc(100vh - 160px)', minHeight: isMobile ? 'calc(100vh - 150px)' : 'calc(100vh - 160px)' }}>
			{filteredSchedules?.map(schedule => (
				<Button key={schedule.ScheduleId}
					component="a"
					href={`/workorder-schedules/${schedule.ScheduleId}`}
					onClick={(e) => handleScheduleSelect(e, schedule.ScheduleId)}
					className={cx(classes['schedule-list-item'], { [classes['selected-item']]: selectedScheduleId === schedule.ScheduleId })}
					leftSection={
						<ThemeIcon radius="xl"
							color={schedule.Deleted ? "red" : "blue"}
							size="xl"
							style={{ border: `1px solid var(--mantine-color-default-border)` }}
							variant="light"
						>
							{!schedule.Deleted ? (schedule.IsActive ? <IconClockPlay size={24} /> : <IconClockPause size={24} color="var(--mantine-color-gray-5)" />) : <IconTrash size={24} />}
						</ThemeIcon>
					}
					justify="space-between"
					fullWidth
				>
					<Stack gap={0} align="flex-start" style={{ flex: 1 }}>
						<Text c={schedule.Deleted ? "dimmed" : ""}>{schedule.ScheduleName}</Text>
						{!schedule.Deleted && <Group gap="5" mt={1}>
							<Badge size="sm" variant="light" radius="sm">{getRecurrenceTypeDescription(schedule.RecurrenceType)}</Badge>
							<Badge size="sm" variant="light" radius="sm" color={getScheduleStatusColor(schedule)}>{getScheduleStatusDescription(schedule)}</Badge>
						</Group>}
					</Stack>
				</Button>
			))}
			{showNoResults && <NoSearchResults searchType="schedules" query={query} setQuery={setQuery} />}
			{schedules?.length === 0 && <>
				<Stack align="center" justify="center" mt="xl" px="sm">
					<ThemeIcon size={100} radius={100} variant="light" color="gray">
						<IconClock size={60} />
					</ThemeIcon>
					<Text c="dimmed">No schedules have been created yet.</Text>
				</Stack>
			</>}
		</ScrollArea>
	</Paper>
};
